import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Store, Plus, TrendingUp, ShoppingCart, Wrench, Hotel, Car,
  LogOut, BarChart3, ArrowRight, Loader2, X, Eye, EyeOff,
  Building2, Layers, Clock, AlertTriangle
} from 'lucide-react';

interface ShopInfo {
  storeId: number;
  name: string;
  shopType: string;
  todayTotal: number;
  todayCount: number;
  allTimeTotal: number;
  apiKey?: string;
}

interface StoreRecord {
  id: number;
  name: string;
  shopType: string | null;
  apiKey: string;
  storeCode: string;
  location: string;
  storeLogo?: string;
  parentStoreId?: number | null;
  approvalStatus?: 'PENDING' | 'APPROVED' | 'REJECTED';
}

interface ShopSelectorLobbyProps {
  currentApiKey: string;
  currentStoreName: string;
  onSelectShop: (shop: { apiKey: string; name: string; shopType: string; storeId: number }) => void;
  onLogout: () => void;
}

const VERTICAL_META: Record<string, { label: string; color: string; bg: string; border: string; icon: React.ReactNode }> = {
  RETAIL:     { label: 'Retail POS',   color: 'text-sky-600',    bg: 'bg-sky-50',    border: 'border-sky-200',   icon: <ShoppingCart size={18} /> },
  AUTO_PARTS: { label: 'Auto Parts',   color: 'text-orange-600', bg: 'bg-orange-50', border: 'border-orange-200', icon: <Wrench size={18} /> },
  HOTEL:      { label: 'Hotel',        color: 'text-violet-600', bg: 'bg-violet-50', border: 'border-violet-200', icon: <Hotel size={18} /> },
  RENTAL:     { label: 'Rental',       color: 'text-emerald-600',bg: 'bg-emerald-50',border: 'border-emerald-200',icon: <Car size={18} /> },
};

const formatRs = (n: number) => `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const ShopSelectorLobby: React.FC<ShopSelectorLobbyProps> = ({
  currentApiKey, currentStoreName, onSelectShop, onLogout
}) => {
  const [stores, setStores] = useState<StoreRecord[]>([]);
  const [overview, setOverview] = useState<{ stores: ShopInfo[]; networkTotal: number; networkAllTime: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [switching, setSwitching] = useState<number | null>(null);

  const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
  const headers: Record<string, string> = {
    'x-store-api-key': currentApiKey,
    'Content-Type': 'application/json',
    ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {}),
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [storesRes, overviewRes] = await Promise.all([
        fetch('/api/my-stores', { headers }),
        fetch('/api/my-stores/network-overview', { headers }),
      ]);
      if (storesRes.ok) setStores(await storesRes.json());
      if (overviewRes.ok) setOverview(await overviewRes.json());
    } catch {}
    setLoading(false);
  };

  useEffect(() => { loadData(); }, []);

  const handleSelect = (store: StoreRecord) => {
    if (store.approvalStatus === 'PENDING') return;
    if (store.approvalStatus === 'REJECTED') return;
    setSwitching(store.id);
    onSelectShop({
      apiKey: store.apiKey,
      name: store.name,
      shopType: store.shopType || 'RETAIL',
      storeId: store.id,
    });
  };

  const getOverview = (storeId: number) => overview?.stores.find(s => s.storeId === storeId);

  return (
    <div className="min-h-screen bg-slate-50 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-sky-100/50 via-slate-50 to-white flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/60 bg-white/60 backdrop-blur-xl">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 flex items-center justify-center shadow-sm">
            <Layers size={18} className="text-white" />
          </div>
          <div>
            <p className="text-[11px] font-mono text-slate-400 uppercase tracking-widest">Kangavena Cloud</p>
            <p className="text-[14px] font-black text-slate-800">Choose Workspace</p>
          </div>
        </div>
        <button onClick={onLogout}
          className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[11px] font-bold text-slate-500 hover:text-rose-600 hover:bg-rose-50 border border-transparent hover:border-rose-200 transition-all">
          <LogOut size={13} /> Sign Out
        </button>
      </div>

      <div className="flex-1 max-w-5xl w-full mx-auto px-4 py-8 space-y-8">

        {/* Network Overview Banner */}
        {overview && (
          <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl bg-gradient-to-r from-sky-500 to-cyan-500 p-5 text-white shadow-lg shadow-sky-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-widest opacity-80 mb-1">Network Overview — Today</p>
                <p className="text-[28px] font-black">{formatRs(overview.networkTotal)}</p>
                <p className="text-[12px] opacity-70 mt-0.5">All-time: {formatRs(overview.networkAllTime)}</p>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-[22px] font-black">{stores.length}</p>
                  <p className="text-[10px] opacity-70 uppercase tracking-wider">Shops</p>
                </div>
                <div className="w-px h-10 bg-white/30" />
                <div className="text-center">
                  <p className="text-[22px] font-black">{overview.stores.reduce((s, st) => s + st.todayCount, 0)}</p>
                  <p className="text-[10px] opacity-70 uppercase tracking-wider">Txns Today</p>
                </div>
                <BarChart3 size={32} className="opacity-30" />
              </div>
            </div>
          </motion.div>
        )}

        {/* Shop Grid */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[13px] font-black text-slate-700 uppercase tracking-wider">Your Shops</h2>
            <motion.button whileTap={{ scale: 0.97 }} onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-[11px] font-bold shadow-sm transition-colors">
              <Plus size={13} /> New Shop
            </motion.button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={24} className="animate-spin text-sky-400" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {stores.map((store, idx) => {
                const meta = VERTICAL_META[store.shopType || 'RETAIL'] || VERTICAL_META.RETAIL;
                const ov = getOverview(store.id);
                const isCurrent = store.apiKey === currentApiKey;
                const isLoading = switching === store.id;
                const isPending  = store.approvalStatus === 'PENDING';
                const isRejected = store.approvalStatus === 'REJECTED';
                const isBlocked  = isPending || isRejected;
                return (
                  <motion.div key={store.id}
                    initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={`relative rounded-2xl border bg-white shadow-sm transition-all group overflow-hidden
                      ${isBlocked ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md cursor-pointer'}
                      ${isCurrent  ? 'ring-2 ring-sky-400 border-sky-200'
                        : isPending  ? 'border-amber-200 bg-amber-50/30'
                        : isRejected ? 'border-red-200 bg-red-50/20'
                        : 'border-slate-200 hover:border-sky-200'}`}
                    onClick={() => !isLoading && handleSelect(store)}>

                    {isCurrent && (
                      <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-sky-100 border border-sky-200 text-[9px] font-black text-sky-600 uppercase tracking-wider">Active</div>
                    )}
                    {isPending && !isCurrent && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-[9px] font-black text-amber-700 uppercase tracking-wider">
                        <Clock size={8} /> Pending
                      </div>
                    )}
                    {isRejected && (
                      <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 border border-red-300 text-[9px] font-black text-red-600 uppercase tracking-wider">
                        <AlertTriangle size={8} /> Rejected
                      </div>
                    )}

                    <div className="p-4">
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${meta.bg} ${meta.border} border mb-3`}>
                        <span className={meta.color}>{meta.icon}</span>
                        <span className={`text-[10px] font-black uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
                      </div>

                      <h3 className="text-[15px] font-black text-slate-800 mb-0.5">{store.name}</h3>
                      <p className="text-[11px] text-slate-400 font-mono mb-4">{store.location || 'No location set'}</p>

                      {ov ? (
                        <div className="flex items-center justify-between text-[11px]">
                          <div>
                            <p className="text-slate-400 font-mono">Today</p>
                            <p className="font-black text-slate-800">{formatRs(ov.todayTotal)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-slate-400 font-mono">Transactions</p>
                            <p className="font-black text-slate-800">{ov.todayCount}</p>
                          </div>
                        </div>
                      ) : (
                        <div className="h-6 bg-slate-50 rounded animate-pulse" />
                      )}
                    </div>

                    <div className={`flex items-center justify-center gap-2 py-3 border-t text-[11px] font-bold transition-colors
                      ${isCurrent   ? 'bg-sky-50   border-sky-100   text-sky-600'
                        : isPending  ? 'bg-amber-50 border-amber-100 text-amber-600'
                        : isRejected ? 'bg-red-50   border-red-100   text-red-500'
                        : 'bg-slate-50 border-slate-100 text-slate-500 group-hover:bg-sky-50 group-hover:text-sky-600'}`}>
                      {isLoading ? (
                        <><Loader2 size={13} className="animate-spin" /> Switching…</>
                      ) : isCurrent ? (
                        <><Building2 size={13} /> Currently Active</>
                      ) : isPending ? (
                        <><Clock size={13} /> Pending Admin Approval</>
                      ) : isRejected ? (
                        <><AlertTriangle size={13} /> Rejected by Admin</>
                      ) : (
                        <><ArrowRight size={13} /> Switch to this shop</>
                      )}
                    </div>
                  </motion.div>
                );
              })}

              {/* Add shop card */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: stores.length * 0.05 }}
                onClick={() => setShowCreateModal(true)}
                className="rounded-2xl border-2 border-dashed border-slate-200 bg-white/50 hover:border-sky-300 hover:bg-sky-50/50 flex flex-col items-center justify-center gap-2 p-8 cursor-pointer transition-all group min-h-[180px]">
                <div className="w-10 h-10 rounded-xl bg-slate-100 group-hover:bg-sky-100 flex items-center justify-center transition-colors">
                  <Plus size={20} className="text-slate-400 group-hover:text-sky-500 transition-colors" />
                </div>
                <p className="text-[12px] font-bold text-slate-500 group-hover:text-sky-600 transition-colors">Add New Shop</p>
                <p className="text-[10px] text-slate-400 text-center">Retail, Auto Parts, Hotel or Rental</p>
              </motion.div>
            </div>
          )}
        </div>
      </div>

      {/* Create Shop Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateShopModal
            apiKey={currentApiKey}
            onClose={() => setShowCreateModal(false)}
            onCreated={(store) => {
              setShowCreateModal(false);
              loadData();
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

/* ── Create Shop Modal ── */
const CreateShopModal: React.FC<{
  apiKey: string;
  onClose: () => void;
  onCreated: (store: StoreRecord) => void;
}> = ({ apiKey, onClose, onCreated }) => {
  const [form, setForm] = useState({ name: '', password: '', confirmPassword: '', shopType: 'RETAIL', location: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPass, setShowPass] = useState(false);

  const verticals = [
    { value: 'RETAIL',     label: 'Retail POS',   desc: 'Supermarket, pharmacy, general store',      icon: <ShoppingCart size={16} />, color: 'sky' },
    { value: 'AUTO_PARTS', label: 'Auto Parts',   desc: 'Vehicle parts shop, garage, workshop',      icon: <Wrench size={16} />,       color: 'orange' },
    { value: 'HOTEL',      label: 'Hotel',        desc: 'Guest house, lodge, boutique hotel',        icon: <Hotel size={16} />,        color: 'violet' },
    { value: 'RENTAL',     label: 'Rental',       desc: 'Vehicle or equipment rental',               icon: <Car size={16} />,          color: 'emerald' },
  ] as const;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      // POST /api/my-stores/create requires a cashier JWT (CASHIER_JWT_SECRET).
      // Never use adminToken here — different secret, will always fail verifyCashierToken.
      const cashierToken = localStorage.getItem('kangavena:cashierToken') || '';
      if (!cashierToken) {
        setError('Manager sign-in required — please sign back into your POS account.');
        setLoading(false);
        return;
      }
      // Client-side expiry check (server is still the security gate)
      try {
        const parts = cashierToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
          if (payload?.exp && payload.exp <= Math.floor(Date.now() / 1000)) {
            localStorage.removeItem('kangavena:cashierToken');
            setError('Session expired — please sign back in via the cashier badge, then try again.');
            setLoading(false);
            return;
          }
        }
      } catch { /* malformed token — let server reject it */ }

      const res = await fetch('/api/my-stores/create', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': apiKey,
          'Authorization': `Bearer ${cashierToken}`,
        },
        body: JSON.stringify({ name: form.name, password: form.password, shopType: form.shopType, location: form.location }),
      });
      const data = await res.json();
      // 401 = stale/wrong-secret token → clear it so re-login issues fresh token
      if (res.status === 401) {
        localStorage.removeItem('kangavena:cashierToken');
        setError('Session invalid — please sign back in via the cashier badge, then try again.');
        return;
      }
      if (!res.ok) { setError(data.error || 'Failed to create shop'); return; }
      onCreated(data.store);
    } catch { setError('Cannot reach server'); }
    setLoading(false);
  };

  const colorMap: Record<string, string> = {
    sky: 'border-sky-400 bg-sky-50 text-sky-700',
    orange: 'border-orange-400 bg-orange-50 text-orange-700',
    violet: 'border-violet-400 bg-violet-50 text-violet-700',
    emerald: 'border-emerald-400 bg-emerald-50 text-emerald-700',
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div initial={{ scale: 0.95, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 8 }}
        className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-[15px] font-black text-slate-800">Create New Shop</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400">
            <X size={15} />
          </button>
        </div>

        <form onSubmit={handleCreate} className="p-6 space-y-5">
          {/* Vertical selector */}
          <div>
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-2 block">Shop Type</label>
            <div className="grid grid-cols-2 gap-2">
              {verticals.map(v => (
                <button key={v.value} type="button"
                  onClick={() => setForm(f => ({ ...f, shopType: v.value }))}
                  className={`flex items-start gap-2.5 p-3 rounded-xl border-2 text-left transition-all
                    ${form.shopType === v.value ? colorMap[v.color] + ' border-2' : 'border-slate-200 hover:border-slate-300 bg-white'}`}>
                  <span className="mt-0.5 shrink-0">{v.icon}</span>
                  <div>
                    <p className="text-[11px] font-black">{v.label}</p>
                    <p className="text-[9px] opacity-60">{v.desc}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Shop Name</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Indunil Auto Parts" required maxLength={60}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20" />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Location</label>
            <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))}
              placeholder="e.g. Colombo 05"
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Password</label>
              <div className="relative">
                <input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  type={showPass ? 'text' : 'password'} required minLength={6}
                  className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20 pr-9" />
                <button type="button" onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPass ? <EyeOff size={12} /> : <Eye size={12} />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Confirm</label>
              <input value={form.confirmPassword} onChange={e => setForm(f => ({ ...f, confirmPassword: e.target.value }))}
                type={showPass ? 'text' : 'password'} required
                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-[13px] text-slate-800 outline-none focus:border-sky-400 focus:ring-2 focus:ring-sky-400/20" />
            </div>
          </div>

          {error && <p className="text-[11px] text-rose-600 font-mono bg-rose-50 px-3 py-2 rounded-lg">{error}</p>}

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-[12px] font-bold text-slate-600 hover:bg-slate-50 transition-colors">
              Cancel
            </button>
            <motion.button whileTap={{ scale: 0.97 }} type="submit" disabled={loading}
              className="flex-1 py-2.5 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-[12px] font-black flex items-center justify-center gap-2 disabled:opacity-50 transition-colors">
              {loading ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              {loading ? 'Creating…' : 'Create Shop'}
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
